<template>
  <v-flex xs12>
    <!-- fecha -->
    <v-flex xs12>
      <date-input
        :loading="loadingOpeningHours"
        class="flex"
        :label="$t('fields.date')"
        :errorMessages="state.formErrors ? state.formErrors.date : null"
        :hint="
          !validBookings
            ? $t('activity_reservation.warning.no_checkin_or_checkout')
            : null
        "
        :disabled="!validBookings"
        persistent-hint
        @change="v => (date = v)"
        @updateMonth="(month) => updateMonth(month)"
        :rules="[rules.required]"
        :initialValue="date"
        required
        :allowedDates="val => allowedDates(val)"
      />
    </v-flex>
    <!-- time slot -->
    <v-flex xs12 :key="availableSlotsForSelectedDate.length">
      <app-dropdown
        :loading="loadingOpeningHours"
        :disabled="!date || !availableSlotsForSelectedDate.length"
        :label="$t('fields.time')"
        :items="date ? availableSlotsForSelectedDate : []"
        class="notranslate required"
        filled
        :rules="[rules.required]"
        itemValue="hour"
        itemText="displayHour"
        itemDisabled="disabled"
        :value="slot"
        @change="value => (slot = value)"
        prependInnerIcon="mdi-clock-outline"
        hasSelectionSlot
        :hasItemSlot="false"
        :hint="hintSlotHours"
        :persistentHint="hintSlotHours ? true : false"
      >
        <template v-slot:selection="{ item }">
          {{ item.displayHour }}
        </template>
        <template v-slot:item="{ item }">
          <v-layout space-between :class="item.class">
            {{ item.displayHour }}
            <v-spacer />
            {{
              item.reachedMaxCapacity
                ? $t("activity_reservation.reached_capacity")
                : ""
            }}
          </v-layout>
        </template>
      </app-dropdown>
      <v-tooltip bottom right v-if="slotDisabledWarnings">
        <template v-slot:activator="{ on, attrs }">
          <div class="d-flex align-center justify-end" v-bind="attrs" v-on="on">
            <span
              v-if="reservationMethod.type === reservationTypes.ROOM_SERVICE"
              class="caption"
            >
              {{ $t('activity_reservation.delivery_terms') }}
            </span>
            <span
              v-else-if="reservationMethod.type === reservationTypes.IN_VENUE"
              class="caption"
            >
              {{ $t('activity_reservation.booking_terms') }}
            </span>
            <v-btn icon color="trasnparent">
              <v-icon>
                mdi-information-outline
              </v-icon>
            </v-btn>
          </div>
        </template>
        <div class="mt-5">
          <div v-if="reservationMethod.leewayTimeBeforeActivityServiceStart && leewayTimeBeforeActivityServiceStartInit && reservationMethod.leewayTimeBeforeActivityServiceStart >= 0" class="mb-5">
            {{
              $t(`activity_reservation.leeway_time_before_activity_service_start_init.${reservationMethod.type}`)
            }}
          </div>
          <div v-if="reservationMethod.leewayTimeBeforeActivityServiceStart && !leewayTimeBeforeActivityServiceStartInit && reservationMethod.leewayTimeBeforeActivityServiceStart >= 0" class="mb-5">
            {{
              $t(`activity_reservation.leeway_time_before_activity_service_start.${reservationMethod.type}`, {time: formatDuration({ ms: reservationMethod.leewayTimeBeforeActivityServiceStart, extended: false })})
            }}
          </div>
          <div v-if="reservationMethod.minimumTimeNotice && reservationMethod.minimumTimeNotice > 0" class="mb-5">
            {{
              $t(`activity_reservation.slot_notification_min.${reservationMethod.type}`, {
                time: formatDuration({ ms: reservationMethod.minimumTimeNotice, extended: false })
              })
            }}
          </div>
          <div v-if="reservationMethod.maximumTimeNotice" class="mb-5">
            {{
              $t(`activity_reservation.slot_notification_max.${reservationMethod.type}`, {
                time: formatDuration({ ms: reservationMethod.maximumTimeNotice, extended: false })
              })
            }}
          </div>
        </div>
      </v-tooltip>
    </v-flex>
  </v-flex>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment-timezone'
import { formatTime, formatDuration } from '@/lib/helpers/HoursFormatter'
import { reservationTypes } from '@/config'
import ActivityMixins from '@/mixins/Activity'
import { getTimeFormat } from '@/lib/helpers/HoursFormatter'

export default {
  props: {
    activityId: String,
    pageId: String,
    reservationMethod: Object,
    initialDate: String,
    initialSlot: Number,
    people: Number,
  },
  data () {
    return {
      loadingOpeningHours: false,
      rules: {
        required: value => !!value || '',
      },
      moment,
      hintSlotHours: null,
      formatDuration,
      leewayTimeBeforeActivityServiceStartInit: false,
      reservationTypes,
      selectedMonth: null,
      visitedMonths: new Set(),
    }
  },
  created () {
    this.getOpeningHours()
    if (this.initialSlot) {
      const slotInMillis = moment.duration(this.initialSlot, 'ms')
      this.slot =
        slotInMillis.hours() +
        ':' +
        (slotInMillis.minutes() <= 9 ? '0' : '') +
        slotInMillis.minutes()
    }
    if (this.initialDate) {
      this.date = this.initialDate
    }
  },
  computed: {
    ...mapState({
      state: state => state.state,
      activityReservations: state => state.activityReservations,
      pages: state => state.pages,
    }),
    ...mapGetters({
      availableDates: 'me/availableDates',
      facilityTimeZone: 'me/facilityTimeZone',
      validBookings: 'me/validBookings',
      firstAvailableDate: 'me/firstAvailableDate',
      lastAvailableDate: 'me/lastAvailableDate',
      isDateTimeInSomeBooking: 'me/isDateTimeInSomeBooking',
    }),
    date: {
      get () {
        return this.activityReservations.edited.date
          ? moment(this.activityReservations.edited.date).format('YYYY-MM-DD')
          : null
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'date',
          value,
        })
      },
    },
    slot: {
      get () {
        return this.activityReservations.edited.slot
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'slot',
          value,
        })
      },
    },
    availableSlotsForSelectedDate () {
      const slots = this.getAvailableSlots(this.date)
      if (!slots.length && this.initialDate === this.date) {
        this.date = null
      }
      return slots
    },
    slotDisabledWarnings () {
      return this.reservationMethod.minimumTimeNotice > 0 || !!this.reservationMethod.maximumTimeNotice || !!this.reservationMethod.leewayTimeBeforeActivityServiceStart
    },
  },
  methods: {
    allowedDates (val) {
      return this.availableDates.includes(val) && !!this.getAvailableSlots(val).length
    },
    getOpeningHours () {
      this.loadingOpeningHours = true
      const endDate = moment(this.selectedMonth || this.firstAvailableDate).endOf('month').format('YYYY-MM-DD')
      return this.$store
        .dispatch('pages/getPageOpeningHours', {
          pageId: this.pageId,
          start: this.firstAvailableDate,
          end: endDate,
          storeSlots: true,
        })
        .then(() => {
          const firstAvailableDate = this.availableDates.find((date) => {
            return this.allowedDates(date)
          })
          /* validate get date in props if no date in props find 
          * first day with available slots
          */
          if (!this.date && firstAvailableDate) this.date = firstAvailableDate
        })
        .finally(() => {
          this.loadingOpeningHours = false
        })
    },
    getSlots (date) {
      this.$store
        .dispatch('pages/getSlots', {
          pageId: this.pageId,
          date,
          save: true,
        })
        .then(() => {
          this.getSlotCapacity({
            slot: this.slot || '00:00',
            activityId: this.activityId,
          }).then(() => {
            const foundSlot = this.availableSlotsForSelectedDate.find(s => {
              return s.hour === this.slot
            })
            if (!foundSlot || foundSlot.disabled) {
              this.slot = null
            }
          })
        })
    },
    getAvailableSlots (date) {
      const storedOpeningHours = this.pages.storedSlots[date] || null
      let availableSlots = []

      if (storedOpeningHours && this.reservationMethod) {
        let openingHours = JSON.parse(JSON.stringify(storedOpeningHours))
        let beforeDay = moment.tz(date, this.facilityTimeZone).subtract(1, 'days').format('YYYY-MM-DD')
        let afterDay = moment.tz(date, this.facilityTimeZone).add(1, 'days').format('YYYY-MM-DD')

        const openingHoursAfterDay = this.pages.storedSlots[afterDay] || null
        const openingHoursBeforeDay = this.pages.storedSlots[beforeDay] || null

        if (
          storedOpeningHours.availableSlots.some(({ end }) => end >= 86399999) || 
          storedOpeningHours.availableSlots.some(({ start }) => start === 0)
        ) {
          const slotAfter = openingHoursAfterDay ? openingHoursAfterDay.availableSlots.find(({ start }) => start === 0) : null
          const slotBefore = openingHoursBeforeDay ? openingHoursBeforeDay.availableSlots.find(({ end }) => end >= 86399999) : null

          const convertDate = (dateConverter,time) => moment.tz(dateConverter, this.facilityTimeZone).add(time , 'ms').format()

          openingHours.availableSlots =  openingHours.availableSlots.map((slot) => {
            slot.start = {convertDate: convertDate(date, slot.start) , timeSeconds: slot.start}
            slot.end = {convertDate: convertDate(date, slot.end), timeSeconds:slot.end}

            if (slot.end.timeSeconds >= 86399999 && slotAfter) {
              slot.end = {convertDate:convertDate(afterDay, slotAfter.end), timeSeconds:slotAfter.end}
            }
            if (slot.start.timeSeconds === 0 && slotBefore) {
              slot.start = {convertDate:convertDate(beforeDay, slotBefore.start), timeSeconds:slotBefore.start}
            }
            return slot
          })
        } else {
          const convertDate = (dateConverter,time) => moment.tz(dateConverter, this.facilityTimeZone).add(time , 'ms').format()

          openingHours.availableSlots =  openingHours.availableSlots.map((slot) => {
            slot.start = {convertDate: convertDate(date, slot.start) , timeSeconds: slot.start}
            slot.end = {convertDate: convertDate(date, slot.end), timeSeconds:slot.end}
            if (slot.end.timeSeconds === 0) {
              slot.end.convertDate = convertDate(afterDay, slot.end)
            }
            return slot
          })
        }

      

        const {
          minimumTimeNotice,
          maximumTimeNotice,
          reservationTimeInterval,
          attendeeTypeConfiguration,
          leewayTimeBeforeActivityServiceStart,
        } = this.reservationMethod

        const availableHours = openingHours.availableSlots
        const leewayMoment = moment().add(leewayTimeBeforeActivityServiceStart, 'ms')
        const minimumTimeNoticeMoment = moment().add(minimumTimeNotice, 'ms')
        const maximumTimeNoticeMoment = moment().add(maximumTimeNotice, 'ms')

        availableHours.forEach(({ start, end }) => {
          for (
            let time = JSON.parse(JSON.stringify(start));
            moment(time.convertDate).add(reservationTimeInterval, 'ms').isSameOrBefore(moment(end.convertDate));
            (time.timeSeconds += reservationTimeInterval,
            time.convertDate = moment.tz(time.convertDate, this.facilityTimeZone).add(reservationTimeInterval, 'ms').format())
          ) {

            if (!openingHours.excludedSlots.includes(time.timeSeconds)) {
              const reachedMaxCapacity =
                attendeeTypeConfiguration !== 'DISABLED' &&
                this.getCapacityStatus({
                  over: true,
                  slot: formatTime(time.timeSeconds),
                  curr: this.people || 0,
                })

              const hourRange = moment.tz(date, 'YYYY-MM-DD', this.facilityTimeZone).add(start.timeSeconds, 'ms')
              const slotMoment = moment(time.convertDate)

              let data = {
                hour: formatTime(time.timeSeconds),
                displayHour: moment.utc(time.timeSeconds).format(getTimeFormat()),
                date: time.convertDate,
                value: time.timeSeconds,
                disabled: reachedMaxCapacity || !this.isDateTimeInSomeBooking(slotMoment),
                notification:
                  reachedMaxCapacity || !this.isDateTimeInSomeBooking(slotMoment)
                    ? this.$t('activity_reservation.fully_booked')
                    : null,
                icon: 'mdi-emoticon-sad-outline',
                reachedMaxCapacity,
              }
              // Add slot to availableSlots
              // If slot is after current moment
              if (slotMoment.isAfter(moment())) {
                // If there is leeway before service start and has already started
                if (leewayTimeBeforeActivityServiceStart !== null && leewayMoment.isAfter(hourRange)) {
                  data.disabled = true
                }
                // If there is max time notice and has passed
                if (maximumTimeNotice && maximumTimeNoticeMoment.isBefore(slotMoment)) {
                  data.disabled = true
                }
                // If there is min time notice
                else if (minimumTimeNotice && minimumTimeNoticeMoment.isAfter(slotMoment)) {
                  data.disabled = true
                }
                if (!data.disabled) {
                  availableSlots.push(data)
                }
              }
            }
          }
        })

        availableSlots = availableSlots.sort((a, b) => {
          return moment(a.hour, 'HH:mm').isBefore(moment(b.hour, 'HH:mm'))
            ? -1
            : 1
        }).filter((slot) => 
          moment.tz(date, this.facilityTimeZone).format('YYYY-MM-DD') === moment.tz(slot.date, this.facilityTimeZone).format('YYYY-MM-DD')
        )
      }
      return availableSlots
    },
    updateMonth (month) {
      this.selectedMonth = month
      if (moment(this.selectedMonth).isBetween(moment(this.firstAvailableDate), moment(this.lastAvailableDate)) &&
          !this.visitedMonths.has(month)) {
        this.visitedMonths.add(month)
        this.getOpeningHours()
      }
    },
  },
  watch: {
    date: {
      handler: function () {
        if (this.date) {
          this.getSlots(this.date)
          if (this.slot) {
            this.$emit('checkAnotherReservation', { date: this.date, slot: this.slot })
          }
        }
      },
      immediate: true,
    },
    slot () {
      if (this.slot) {
        this.$emit('checkAnotherReservation', { date: this.date, slot: this.slot })
      }
    },
  },
  mixins: [ActivityMixins],
  components: {
    DateInput: () => import('@/components/app-components/DateInput.vue'),
    AppDropdown: () => import('@/components/app-components/AppDropdown.vue'),
  },
}
</script>

<style lang="scss">
.notice {
  padding: 16px 10px;
  background: var(--v-warning-lighten) !important;
  color: var(--v-warning-darken) !important;
}
</style>
